import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryOptions_signed_key_project } from "@/lib/requests/file/file.get-signed-key";
import { cn } from "@/lib/style/cva.config";
import defaultGradient from "../../../../assets/boomer/default_gradient.jpg";

export interface ProjectImageProps {
  /** Project waar de afbeelding bij hoort; nodig om de URL te ondertekenen. */
  projectId?: string | null;
  /** Opslagsleutel van de afbeelding (`project.image`). Leeg = geen afbeelding. */
  imageKey?: string | null;
  alt: string;
  /**
   * Klassen voor het kader. Hier hoort ALTIJD `aspect-video` in te staan, met een breedte
   * (`aspect-video w-full`, `aspect-video w-16`, …). Dat is 16:9, gelijk aan het 1600x900 waarin de
   * afbeelding wordt opgeslagen, zodat `object-cover` er niets meer af haalt. Wijkt een kader
   * daarvan af, dan snijdt het — en sinds app#785 is er geen focuspunt meer dat dat opvangt.
   * `project-image.guard.test.ts` faalt als een kader wegloopt.
   */
  className?: string;
  /** Inhoud bovenop de afbeelding, zoals een badge. */
  children?: ReactNode;
}

/**
 * De enige plek in de app die een projectafbeelding tekent.
 *
 * Waarom één component (app#114): dezelfde afbeelding wordt op tien plekken getoond, in kaders die
 * toen van 5:4 tot 3,4:1 liepen. `object-cover` snijdt bij élke afwijkende verhouding, standaard
 * vanuit het midden — dus wat de planner in de admin mooi in beeld zette, viel hier weg. Losse
 * `<img>`-blokken betekende ook net zoveel plekken die dat elk apart moesten oplossen (en één die
 * zijn `enabled`-guard miste en bij projecten zónder afbeelding een verzoek met lege sleutel
 * afvuurde).
 *
 * Sinds app#785 staat élk kader op 16:9, gelijk aan de opslag, en is er dus niets meer bij te
 * snijden. Het focuspunt dat een afwijkend kader nog kon redden is daarmee weg — wat betekent dat
 * een kader dat alsnog wegloopt gewoon beeld kost. Daar zit de guard-test op.
 *
 * Deze component is de opvolger van `project/tmp/signed-rectangle-image.tsx`: dezelfde
 * ondertekende-URL-en-terugval, maar met een aanroepvorm die het project beschrijft in plaats van
 * query-opties door te geven — daardoor kan de `enabled`-guard hier staan en niet bij elke
 * aanroeper opnieuw.
 */
export function ProjectImage({ projectId, imageKey, alt, className, children }: ProjectImageProps) {
  const hasImage = !!projectId && !!imageKey;

  const { data } = useQuery({
    ...queryOptions_signed_key_project([projectId ?? "", imageKey ?? ""]),
    // Eén guard voor alle aanroepers. Zonder deze regel vuurt een project zonder
    // afbeelding een GET /file/project/{id}/ met lege sleutel af.
    enabled: hasImage,
  });

  const signedUrl = data?.url;

  return (
    <div className={cn("relative overflow-hidden", className)}>
      <img src={signedUrl || defaultGradient} alt={alt} className="h-full w-full object-cover" />
      {children}
    </div>
  );
}
